import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode, SetStateAction } from 'react';
import { Badge, Button, Icon, cardClasses, cn } from '@evogard/ui';
import type { Tone } from '@evogard/domain';

/**
 * O mapa do time — o organograma que se arrasta e se aproxima, e a mesma
 * árvore em tabela (29/10/2026). As duas telas que o usam (Financeiro e
 * Sinistro) trazem a árvore pronta da API e dizem quais colunas mostrar; aqui
 * só se desenha.
 *
 * O organograma põe a praça e os gestores no alto, os times numa linha e o
 * líder e os consultores de cada time empilhados embaixo dele — o time é
 * largo e raso, e empilhar é o que faz a CE inteira caber na tela sem um zoom
 * ilegível. Arrasta com o ponteiro ou o dedo, aproxima com a roda, a pinça ou
 * os botões, e pelo teclado: setas movem, `+` e `-` aproximam e afastam, `0`
 * ajusta, e o Tab passa de cartão em cartão com a câmera indo atrás.
 */

/** O que todo nó tem — o resto (os números) é de cada tela. */
export interface TeamMapNodeShape {
  readonly key: string;
  readonly parentKey: string | null;
  readonly kind: 'praca' | 'gestor' | 'time' | 'consultor' | 'grupo' | 'lacuna';
  readonly label: string;
  readonly sub: string;
  readonly leader: boolean;
  readonly photoUrl: string | null;
}

/** As cores das séries de gráfico da casa — as mesmas que o `StatCard` aceita em `legend`. */
export type TeamMapDot =
  | 'fase-azul'
  | 'fase-turquesa'
  | 'fase-vermelho'
  | 'fase-verde'
  | 'fase-laranja'
  | 'fase-roxo';

const DOTS: Record<TeamMapDot, string> = {
  'fase-azul': 'bg-fase-azul',
  'fase-turquesa': 'bg-fase-turquesa',
  'fase-vermelho': 'bg-fase-vermelho',
  'fase-verde': 'bg-fase-verde',
  'fase-laranja': 'bg-fase-laranja',
  'fase-roxo': 'bg-fase-roxo',
};

export function TeamMapLegendDot({ dot }: { dot: TeamMapDot }) {
  return <span aria-hidden className={cn('inline-block size-2 rounded-sm', DOTS[dot])} />;
}

export interface TeamMapColumn<N> {
  readonly key: string;
  readonly label: string;
  readonly dot?: TeamMapDot;
  readonly value: (node: N) => number;
  readonly format: (value: number) => string;
  /** Mostra a barra proporcional na tabela — os números que se comparam entre irmãos. */
  readonly bar?: boolean;
  /** Aparece também no cartão do organograma, que tem lugar para três ou quatro números. */
  readonly inCard?: boolean;
}

export interface TeamMapProps<N extends TeamMapNodeShape> {
  readonly view: 'organograma' | 'tabela';
  readonly nodes: readonly N[];
  /** As colunas de número — no cartão do organograma, as que têm `dot`. */
  readonly columns: readonly TeamMapColumn<N>[];
  /** O indicador do nó (a sinistralidade, o saldo), em pílula. */
  readonly badge?: (node: N) => { tone: Tone; text: string } | null;
  /** A ordem dos irmãos — o maior primeiro. O líder vai sempre na frente do time dele. */
  readonly rank: (node: N) => number;
  /** A linha de rodapé do cartão ("1.240 veículos"). */
  readonly footer: (node: N) => string;
  readonly selected: string | null;
  readonly onSelect: (key: string) => void;
  /**
   * A altura da área do organograma — a tela diz, quando cresce (largura
   * total, tela cheia). Sem ela, a de sempre. Mudar a altura reajusta a câmera.
   */
  readonly height?: string;
}

/**
 * O abrir e o recolher de um time deslizam (03/11/2026). Recolher é em dois
 * tempos: os filhos saem deslizando e SÓ ENTÃO o time recolhe — sem isso, a
 * linha some antes de a animação começar, e não há o que animar.
 */
type Motion = 'in' | 'out';
const SLIDE_MS = 260;
/** O degrau da cascata no organograma, e o teto dela — time de 30 não espera um segundo. */
const STAGGER_MS = 30;
const STAGGER_MAX_MS = 240;

const reducedMotion = () =>
  typeof matchMedia === 'function' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches;

type Tree<N> = { byKey: Map<string, N>; children: Map<string, N[]>; root: N | undefined };

function buildTree<N extends TeamMapNodeShape>(
  nodes: readonly N[],
  rank: (n: N) => number,
): Tree<N> {
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const children = new Map<string, N[]>();
  for (const n of nodes) {
    if (n.parentKey === null) continue;
    children.set(n.parentKey, [...(children.get(n.parentKey) ?? []), n]);
  }
  const last = (n: N) => (n.kind === 'grupo' || n.label === 'Sem time' ? 1 : 0);
  for (const list of children.values()) {
    list.sort(
      (a, b) =>
        last(a) - last(b) || Number(b.leader) - Number(a.leader) || rank(b) - rank(a),
    );
  }
  return { byKey, children, root: nodes.find((n) => n.parentKey === null) };
}

export function TeamMap<N extends TeamMapNodeShape>(props: TeamMapProps<N>) {
  const tree = useMemo(
    () => buildTree(props.nodes, props.rank),
    [props.nodes, props.rank],
  );
  /* Recolhido é a exceção: o mapa abre inteiro, e quem fecha um time escolheu fechar. */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  /* O time que está deslizando agora, e para que lado. */
  const [motion, setMotion] = useState<ReadonlyMap<string, Motion>>(new Map());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const setCollapsedKey = useCallback((key: string, value: boolean) => {
    setCollapsed((prev) => {
      if (prev.has(key) === value) return prev;
      const next = new Set(prev);
      if (value) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const setMotionKey = useCallback((key: string, value: Motion | null) => {
    setMotion((prev) => {
      const next = new Map(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (key: string) => {
      const kidCount = tree.children.get(key)?.length ?? 0;
      const wasClosing = motion.get(key) === 'out';
      const isOpen = !collapsed.has(key) && !wasClosing;
      clearTimeout(timers.current.get(key));
      timers.current.delete(key);

      if (reducedMotion()) {
        setMotionKey(key, null);
        setCollapsedKey(key, isOpen);
        return;
      }
      /* Clicar de novo no meio da saída desfaz: os filhos voltam deslizando. */
      if (wasClosing) {
        setMotionKey(key, 'in');
      } else if (isOpen) {
        setMotionKey(key, 'out');
        timers.current.set(
          key,
          setTimeout(
            () => {
              timers.current.delete(key);
              setCollapsedKey(key, true);
              setMotionKey(key, null);
            },
            SLIDE_MS + Math.min(kidCount * STAGGER_MS, STAGGER_MAX_MS),
          ),
        );
        return;
      } else {
        setCollapsedKey(key, false);
        setMotionKey(key, 'in');
      }
      timers.current.set(
        key,
        setTimeout(() => {
          timers.current.delete(key);
          setMotionKey(key, null);
        }, SLIDE_MS + STAGGER_MAX_MS),
      );
    },
    [tree, collapsed, motion, setCollapsedKey, setMotionKey],
  );
  if (!tree.root) return null;
  const inner = { ...props, tree, collapsed, motion, onToggle: toggle };
  return props.view === 'organograma' ? (
    <OrgChart {...inner} />
  ) : (
    <TreeTable {...inner} />
  );
}

type Inner<N extends TeamMapNodeShape> = TeamMapProps<N> & {
  tree: Tree<N>;
  collapsed: Set<string>;
  motion: ReadonlyMap<string, Motion>;
  onToggle: (key: string) => void;
};

/* ---------------------------------------------------------------- organograma */

const COL = 272;
const CW = 240;
const H_TOP = 124;
const H_LEAF = 108;
const GAP_V = 12;
const INDENT = 20;
const ROW_GAP = 64;

type Placed<N> = { node: N; x: number; y: number; w: number; h: number };

/**
 * A posição de cada cartão. Tudo acima dos times (praça e gestores) é uma
 * árvore comum, centrada sobre os filhos; o time é uma coluna, com os
 * consultores empilhados embaixo dele.
 */
function layout<N extends TeamMapNodeShape>(tree: Tree<N>, collapsed: Set<string>) {
  const placed: Placed<N>[] = [];
  const lines: string[] = [];
  const kids = (n: N) => tree.children.get(n.key) ?? [];
  const isColumn = (n: N) => n.kind === 'time' || n.kind === 'grupo';
  let cursor = 0;
  let bottom = 0;

  /* Devolve o centro x do nó; `depth` é a linha em que ele fica. */
  const place = (n: N, depth: number): number => {
    const y = depth * (H_TOP + ROW_GAP);
    if (isColumn(n) || kids(n).length === 0 || collapsed.has(n.key)) {
      const x = cursor;
      cursor += COL;
      placed.push({ node: n, x, y, w: CW, h: H_TOP });
      bottom = Math.max(bottom, y + H_TOP);
      if (isColumn(n) && !collapsed.has(n.key)) {
        let cy = y + H_TOP + 22;
        let lastMid = 0;
        for (const k of kids(n)) {
          placed.push({ node: k, x: x + INDENT, y: cy, w: CW - INDENT, h: H_LEAF });
          lines.push(`M${x + 12},${cy + H_LEAF / 2}H${x + INDENT}`);
          lastMid = cy + H_LEAF / 2;
          cy += H_LEAF + GAP_V;
        }
        if (kids(n).length) lines.push(`M${x + 12},${y + H_TOP}V${lastMid}`);
        bottom = Math.max(bottom, cy);
      }
      return x + CW / 2;
    }
    const centers = kids(n).map((k) => place(k, depth + 1));
    const cx = (centers[0]! + centers[centers.length - 1]!) / 2;
    placed.push({ node: n, x: cx - CW / 2, y, w: CW, h: H_TOP });
    const mid = y + H_TOP + ROW_GAP / 2;
    lines.push(`M${cx},${y + H_TOP}V${mid}`);
    lines.push(`M${centers[0]},${mid}H${centers[centers.length - 1]}`);
    for (const c of centers) lines.push(`M${c},${mid}V${mid + ROW_GAP / 2}`);
    return cx;
  };
  place(tree.root!, 0);
  return { placed, lines, width: cursor - (COL - CW), height: bottom + 24 };
}

type Camera = { x: number; y: number; k: number };
const clampK = (k: number) => Math.max(0.3, Math.min(2, k));

/*
 * O movimento que a tela faz sozinha desliza; o que segue a mão não. Sem isto,
 * o clique num cartão meio escondido, o "Ajustar" e o abrir de um time pulavam
 * de um quadro para o outro, e o salto parecia a tela recarregando.
 */
const GLIDE = '320ms var(--ease-saida)';

function OrgChart<N extends TeamMapNodeShape>(props: Inner<N>) {
  const { tree, collapsed, motion } = props;
  const world = useMemo(() => layout(tree, collapsed), [tree, collapsed]);
  const viewport = useRef<HTMLDivElement>(null);
  const [cam, setCamera] = useState<Camera>({ x: 0, y: 24, k: 1 });
  const fitted = useRef(false);
  /* Lido no render: quem muda a câmera diz, junto, se ela desliza até lá. */
  const glide = useRef(false);
  const setCam = useCallback((next: SetStateAction<Camera>, smooth = false) => {
    glide.current = smooth;
    setCamera(next);
  }, []);

  const fit = useCallback(
    (smooth = true) => {
      const el = viewport.current;
      if (!el || !el.clientWidth) return;
      const k = clampK(
        Math.min(
          1,
          (el.clientWidth - 40) / world.width,
          (el.clientHeight - 70) / Math.min(world.height, 900),
        ),
      );
      setCam({ k, x: (el.clientWidth - world.width * k) / 2, y: 24 }, smooth);
    },
    [world.width, world.height, setCam],
  );

  useLayoutEffect(() => {
    if (!fitted.current) {
      fit(false);
      fitted.current = true;
    }
  }, [fit]);

  /*
   * A área cresceu ou encolheu (largura total, tela cheia): a câmera reajusta.
   * Mede pelo próprio elemento, e não pela prop, porque a largura total muda
   * a LARGURA sem mudar a altura, e a tela cheia nativa chega depois do clique.
   */
  const lastSize = useRef<string | null>(null);
  useEffect(() => {
    const el = viewport.current;
    if (!el || typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(() => {
      const size = `${el.clientWidth}x${el.clientHeight}`;
      if (lastSize.current !== null && lastSize.current !== size) fit(true);
      lastSize.current = size;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit]);

  const zoomAt = useCallback(
    (factor: number, px?: number, py?: number, smooth = true) => {
      const el = viewport.current;
      if (!el) return;
      const cx = px ?? el.clientWidth / 2;
      const cy = py ?? el.clientHeight / 2;
      setCam((c) => {
        const k = clampK(c.k * factor);
        return { k, x: cx - (cx - c.x) * (k / c.k), y: cy - (cy - c.y) * (k / c.k) };
      }, smooth);
    },
    [setCam],
  );

  /* A roda precisa de `passive: false` para não rolar a página junto — o React não deixa no `onWheel`. */
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top, false);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const pointers = useRef(
    new Map<number, { x: number; y: number; x0: number; y0: number }>(),
  );
  const dragged = useRef(false);
  const pinch = useRef<{ d: number } | null>(null);
  const [grabbing, setGrabbing] = useState(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      x0: e.clientX,
      y0: e.clientY,
    });
    dragged.current = false;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y) };
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = pointers.current.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    if (!dragged.current && Math.hypot(p.x - p.x0, p.y - p.y0) > 4) {
      dragged.current = true;
      setGrabbing(true);
      viewport.current?.setPointerCapture(e.pointerId);
    }
    if (!dragged.current) return;
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const r = viewport.current!.getBoundingClientRect();
      zoomAt(
        d / pinch.current.d,
        (a.x + b.x) / 2 - r.left,
        (a.y + b.y) / 2 - r.top,
        false,
      );
      pinch.current = { d };
    } else if (pointers.current.size === 1) {
      setCam((c) => ({ ...c, x: c.x + dx, y: c.y + dy }));
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) setGrabbing(false);
  };
  /* O arrasto que termina em cima de um cartão não é clique nele. */
  const onClickCapture = (e: React.MouseEvent) => {
    if (dragged.current) {
      e.stopPropagation();
      dragged.current = false;
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const step = 60;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [step, 0],
      ArrowRight: [-step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const m = moves[e.key];
    if (m) {
      e.preventDefault();
      setCam((c) => ({ ...c, x: c.x + m[0], y: c.y + m[1] }), true);
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoomAt(1.2);
    } else if (e.key === '-') {
      e.preventDefault();
      zoomAt(1 / 1.2);
    } else if (e.key === '0') {
      e.preventDefault();
      fit();
    }
  };

  /* Quem navega por Tab não perde o cartão de vista: a câmera vai até ele. */
  const onFocus = (e: React.FocusEvent) => {
    const card = (e.target as HTMLElement).closest('[data-node]');
    const el = viewport.current;
    if (!card || !el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;
    const r = el.getBoundingClientRect();
    const b = card.getBoundingClientRect();
    const margin = 24;
    let dx = 0;
    let dy = 0;
    if (b.left < r.left + margin) dx = r.left + margin - b.left;
    else if (b.right > r.right - margin) dx = r.right - margin - b.right;
    if (b.top < r.top + margin) dy = r.top + margin - b.top;
    else if (b.bottom > r.bottom - 56) dy = r.bottom - 56 - b.bottom;
    if (dx || dy) setCam((c) => ({ ...c, x: c.x + dx, y: c.y + dy }), true);
  };

  return (
    <div className="relative">
      <div
        ref={viewport}
        tabIndex={0}
        role="application"
        aria-roledescription="organograma"
        aria-label="Organograma do time. Setas movem, mais e menos aproximam, zero ajusta à tela, Tab passa pelos cartões."
        className={cn(
          'relative h-[40rem] touch-none overflow-hidden rounded-b-cartao bg-superficie-suave max-sm:h-[32rem]',
          grabbing ? 'cursor-grabbing' : 'cursor-grab',
        )}
        style={{
          backgroundImage:
            'radial-gradient(circle, var(--color-borda-forte) 1px, transparent 1.2px)',
          backgroundSize: '22px 22px',
          height: props.height,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
      >
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.k})`,
            transition: glide.current ? `transform ${GLIDE}` : undefined,
          }}
        >
          {/*
           * As linhas não deslizam com os cartões: somem no abrir e no recolher
           * e voltam quando eles chegam, em vez de apontar para onde o cartão
           * ainda não está. Na saída, apagam já no começo.
           */}
          <svg
            key={[...collapsed].sort().join('|')}
            aria-hidden
            className={cn(
              'pointer-events-none absolute left-0 top-0 overflow-visible animate-in fade-in-0 duration-300 transition-opacity',
              [...motion.values()].includes('out') && 'opacity-0',
            )}
            style={{
              animationDelay: `${[...motion.values()].includes('in') ? SLIDE_MS : 240}ms`,
              animationFillMode: 'backwards',
            }}
            width={world.width}
            height={world.height}
          >
            {world.lines.map((d, i) => (
              <path
                key={i}
                d={d}
                className="fill-none stroke-borda-forte"
                strokeWidth={1.5}
              />
            ))}
          </svg>
          {world.placed.map((p) => (
            <OrgCard key={p.node.key} {...props} placed={p} />
          ))}
        </div>
      </div>
      <div
        role="toolbar"
        aria-label="Zoom do organograma"
        className={cardClasses({
          density: 'densa',
          padding: false,
          className: 'absolute right-3 top-3 flex items-center gap-1 p-1',
        })}
      >
        <Button
          variant="discreto"
          size="sm"
          aria-label="Aproximar"
          onClick={() => zoomAt(1.2)}
        >
          <Icon nome="mais" />
        </Button>
        <Button
          variant="discreto"
          size="sm"
          aria-label="Afastar"
          onClick={() => zoomAt(1 / 1.2)}
        >
          <Icon nome="menos" />
        </Button>
        <Button variant="discreto" size="sm" onClick={() => fit()}>
          Ajustar
        </Button>
        <span className="w-12 text-center text-xs font-semibold tabular-nums text-texto-suave">
          {Math.round(cam.k * 100)}%
        </span>
      </div>
      <p className="pointer-events-none absolute bottom-3 left-3 max-w-[calc(100%-1.5rem)] rounded-pequeno border border-borda bg-superficie px-2.5 py-1 text-xs text-texto-suave">
        Arraste para mover · roda do mouse ou pinça para o zoom · setas e + − no teclado
      </p>
    </div>
  );
}

function OrgCard<N extends TeamMapNodeShape>({
  placed,
  columns,
  badge,
  footer,
  selected,
  onSelect,
  tree,
  collapsed,
  motion,
  onToggle,
}: Inner<N> & { placed: Placed<N> }) {
  const { node, x, y, w, h } = placed;
  const kids = tree.children.get(node.key) ?? [];
  const isColumn = node.kind === 'time' || node.kind === 'grupo';
  const open = !collapsed.has(node.key) && motion.get(node.key) !== 'out';
  /*
   * O consultor de um time que está abrindo ou recolhendo desliza de baixo do
   * cartão do time: em cascata ao abrir (o primeiro antes), ao contrário ao
   * recolher. Desce um degrau — o de um cartão —, não a coluna inteira.
   */
  const parent = node.parentKey ? tree.byKey.get(node.parentKey) : undefined;
  const slide =
    parent && (parent.kind === 'time' || parent.kind === 'grupo')
      ? motion.get(parent.key)
      : undefined;
  const siblings = parent ? (tree.children.get(parent.key) ?? []) : [];
  const index = siblings.indexOf(node);
  const order = slide === 'out' ? siblings.length - 1 - index : index;
  const delay = slide ? Math.min(order * STAGGER_MS, STAGGER_MAX_MS) : 0;
  const step = `${-(H_LEAF + GAP_V)}px`;
  const b = badge?.(node);
  const gap = node.kind === 'lacuna' || node.kind === 'grupo';
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(node.key);
    }
  };
  return (
    <div
      data-node={node.key}
      role="button"
      tabIndex={0}
      aria-pressed={selected === node.key}
      aria-label={`${node.label}, ${node.sub}`}
      onClick={() => onSelect(node.key)}
      onKeyDown={onKeyDown}
      className={cardClasses({
        density: 'densa',
        surface: gap || node.kind === 'praca' ? 'suave' : 'superficie',
        interactive: true,
        padding: false,
        className: cn(
          'absolute grid cursor-pointer content-start gap-2 px-3 py-2.5 select-none',
          slide === 'out'
            ? 'pointer-events-none animate-out fade-out-0'
            : 'animate-in fade-in-0',
          slide ? 'ease-(--ease-saida)' : 'duration-300',
          gap && 'border-dashed shadow-none',
          selected === node.key && 'border-primaria ring-3 ring-primaria-suave',
        ),
      })}
      style={
        {
          left: x,
          top: y,
          width: w,
          height: h,
          /* O time fica por cima: o consultor sai e entra por baixo dele. */
          zIndex: isColumn ? 2 : 1,
          transition: `left ${GLIDE}, top ${GLIDE}, border-color 160ms, box-shadow 200ms var(--ease-saida)`,
          ...(slide && {
            '--tw-enter-translate-y': step,
            '--tw-exit-translate-y': step,
            animationDuration: `${SLIDE_MS}ms`,
            animationDelay: `${delay}ms`,
            animationFillMode: slide === 'out' ? 'forwards' : 'backwards',
          }),
        } as CSSProperties
      }
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <NodeMark node={node} />
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-bold text-texto">{node.label}</p>
          <p className="truncate text-xs text-texto-tenue">{node.sub}</p>
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-2.5 text-xs font-bold tabular-nums text-texto">
        {columns
          .filter((c) => c.inCard)
          .map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 whitespace-nowrap"
              title={c.label}
            >
              {c.dot && <TeamMapLegendDot dot={c.dot} />}
              {c.format(c.value(node))}
            </span>
          ))}
        {b && (
          <Badge tone={b.tone} className="ml-auto whitespace-nowrap">
            {b.text}
          </Badge>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-texto-tenue">
        <span className="tabular-nums">{footer(node)}</span>
        {isColumn && kids.length > 0 && (
          <Button
            variant="discreto"
            size="sm"
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.key);
            }}
          >
            {open ? 'Recolher' : `Mostrar ${kids.length}`}
          </Button>
        )}
      </div>
    </div>
  );
}

function NodeMark({ node }: { node: TeamMapNodeShape }) {
  /*
   * A URL assinada vale uma hora, e o mapa fica aberto na tela cheia: a que
   * venceu volta para as iniciais, em vez de um buraco no cartão. Guarda QUAL
   * URL falhou, porque a próxima leitura traz outra, que vale.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const photo = node.photoUrl !== failedUrl ? node.photoUrl : null;
  const person = node.kind === 'consultor' || node.kind === 'gestor';
  const initials =
    node.kind === 'lacuna' || node.kind === 'grupo'
      ? '—'
      : node.label
          .replace(/^(Time|Comercial) /, '')
          .split(' ')
          .filter((p) => p.length > 2)
          .slice(0, 2)
          .map((p) => p[0])
          .join('')
          .toUpperCase() || node.label.slice(0, 2).toUpperCase();
  const square = node.kind === 'time' || node.kind === 'praca' || node.kind === 'gestor';
  /* A pessoa tem a mesma caixa com ou sem foto: os nomes de um time ficam alinhados. */
  const box = person ? 'size-9' : 'size-7';
  const shape = square ? 'rounded-pequeno' : 'rounded-full';
  if (photo) {
    return (
      <img
        aria-hidden
        alt=""
        src={photo}
        /* Sem isso, arrastar o mapa a partir da foto puxa a imagem, e não o mapa. */
        draggable={false}
        onError={() => setFailedUrl(photo)}
        className={cn('flex-none bg-superficie-suave object-cover', box, shape)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'grid flex-none place-items-center text-[0.6875rem] font-bold',
        box,
        shape,
        node.kind === 'lacuna' || node.kind === 'grupo'
          ? 'border border-dashed border-borda-forte text-texto-tenue'
          : node.kind === 'consultor'
            ? 'bg-fase-laranja-suave text-fase-laranja'
            : 'bg-fase-azul-suave text-fase-azul',
      )}
    >
      {initials}
    </span>
  );
}

/* ---------------------------------------------------------------- tabela */

function TreeTable<N extends TeamMapNodeShape>(props: Inner<N>) {
  const {
    tree,
    collapsed,
    motion,
    onToggle,
    columns,
    badge,
    selected,
    onSelect,
    footer,
  } = props;
  /* A linha herda o deslize do ancestral mais próximo que está abrindo ou recolhendo. */
  const rows: { node: N; depth: number; slide: Motion | undefined }[] = [];
  const walk = (n: N, depth: number, slide: Motion | undefined) => {
    rows.push({ node: n, depth, slide });
    if (collapsed.has(n.key)) return;
    const own = motion.get(n.key) ?? slide;
    for (const k of tree.children.get(n.key) ?? []) walk(k, depth + 1, own);
  };
  walk(tree.root!, 0, undefined);

  /* A barra compara IRMÃOS: o maior de cada nível é a barra cheia. */
  const maxAt = new Map<string, number>();
  for (const { node, depth } of rows) {
    for (const c of columns) {
      const k = `${depth}:${c.key}`;
      maxAt.set(k, Math.max(maxAt.get(k) ?? 0, Math.abs(c.value(node))));
    }
  }

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, key: string) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(key);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[48rem] border-collapse text-sm">
        <thead>
          <tr className="bg-superficie-suave">
            <th scope="col" className="rotulo px-3 py-2.5 text-left text-texto-suave">
              Gestor · time · consultor
            </th>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className="rotulo whitespace-nowrap px-3 py-2.5 text-right text-texto-suave"
              >
                <span className="inline-flex items-center gap-1.5">
                  {c.dot && <TeamMapLegendDot dot={c.dot} />}
                  {c.label}
                </span>
              </th>
            ))}
            {badge && (
              <th
                scope="col"
                className="rotulo px-3 py-2.5 text-right text-texto-suave"
              />
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ node, depth, slide }) => {
            const kids = tree.children.get(node.key) ?? [];
            const open = !collapsed.has(node.key) && motion.get(node.key) !== 'out';
            const b = badge?.(node);
            return (
              <tr
                key={node.key}
                tabIndex={slide === 'out' ? -1 : 0}
                aria-selected={selected === node.key}
                onClick={() => onSelect(node.key)}
                onKeyDown={(e) => onRowKey(e, node.key)}
                className={cn(
                  'cursor-pointer border-b hover:bg-superficie-suave',
                  'transition-[border-color] duration-(--slide-ms)',
                  slide === 'out'
                    ? 'pointer-events-none border-transparent'
                    : 'border-borda',
                  selected === node.key && 'bg-primaria-suave hover:bg-primaria-suave',
                  depth <= 1 && 'font-semibold',
                )}
                style={{ '--slide-ms': `${SLIDE_MS}ms` } as CSSProperties}
              >
                <td className="p-0">
                  <Slide motion={slide} className="px-3 py-2">
                    <div
                      className="flex min-w-72 items-center gap-2"
                      style={{ paddingLeft: depth * 20 }}
                    >
                      {kids.length > 0 ? (
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-label={`${open ? 'Recolher' : 'Expandir'} ${node.label}`}
                          className="grid size-6 flex-none place-items-center rounded-pequeno text-texto-suave hover:bg-superficie-suave"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggle(node.key);
                          }}
                        >
                          <Icon nome={open ? 'seta-baixo' : 'seta-direita'} />
                        </button>
                      ) : (
                        <span className="size-6 flex-none" />
                      )}
                      <NodeMark node={node} />
                      <span className="grid min-w-0 leading-tight">
                        <span className="truncate text-texto">{node.label}</span>
                        <span className="max-w-72 truncate text-xs font-normal text-texto-tenue">
                          {node.sub} · {footer(node)}
                        </span>
                      </span>
                    </div>
                  </Slide>
                </td>
                {columns.map((c) => {
                  const v = c.value(node);
                  const max = maxAt.get(`${depth}:${c.key}`) ?? 0;
                  return (
                    <td
                      key={c.key}
                      className="whitespace-nowrap p-0 text-right tabular-nums"
                    >
                      <Slide motion={slide} className="px-3 py-2">
                        <span className="inline-grid justify-items-end gap-1">
                          {c.format(v)}
                          {c.bar && c.dot && (
                            <span className="block h-1 w-14 overflow-hidden rounded-full bg-borda">
                              <span
                                className={cn('block h-full rounded-full', DOTS[c.dot])}
                                style={{
                                  width: `${max ? Math.max(2, (Math.abs(v) / max) * 100) : 0}%`,
                                }}
                              />
                            </span>
                          )}
                        </span>
                      </Slide>
                    </td>
                  );
                })}
                {badge && (
                  <td className="p-0 text-right">
                    <Slide motion={slide} className="px-3 py-2">
                      {b && <Badge tone={b.tone}>{b.text}</Badge>}
                    </Slide>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * O conteúdo de uma célula que desliza. `<tr>` não anima altura, então quem
 * anima é a célula: a grade vai de `0fr` a `1fr` e o conteúdo, cortado, abre
 * de cima para baixo — as linhas de baixo descem junto, em vez de pular.
 *
 * A entrada usa `@starting-style` (`starting:`), que vale só na PRIMEIRA
 * pintura do elemento: a linha que já estava na tela não anima de novo.
 *
 * O corte é `overflow-y: clip`, e não `overflow: hidden`: o `hidden` zera a
 * largura mínima da célula, e a tabela espremia as colunas — o nome truncava
 * e a pílula quebrava em duas linhas (medido no navegador em 03/11/2026).
 */
function Slide({
  motion,
  className,
  children,
}: {
  motion: Motion | undefined;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-(--slide-ms) ease-(--ease-saida)',
        motion === 'out' ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
        motion === 'in' && 'starting:grid-rows-[0fr] starting:opacity-0',
      )}
    >
      <div className="min-h-0 overflow-y-clip">
        <div className={className}>{children}</div>
      </div>
    </div>
  );
}

/** Acha um nó e o pai dele — para a gaveta dizer "Consultor · Time Aldeota". */
export function findNode<N extends TeamMapNodeShape>(
  nodes: readonly N[],
  key: string | null,
) {
  const node =
    nodes.find((n) => n.key === key) ?? nodes.find((n) => n.parentKey === null);
  const parent = node?.parentKey
    ? nodes.find((n) => n.key === node.parentKey)
    : undefined;
  return { node, parent };
}

/** As folhas de dentro de um nó — a lista "quem pesa mais aqui". */
export function leavesOf<N extends TeamMapNodeShape>(
  nodes: readonly N[],
  key: string,
): N[] {
  const out: N[] = [];
  const walk = (k: string) => {
    const kids = nodes.filter((n) => n.parentKey === k);
    if (kids.length === 0) {
      const self = nodes.find((n) => n.key === k);
      if (self && self.key !== key) out.push(self);
    }
    for (const c of kids) walk(c.key);
  };
  walk(key);
  return out;
}

/** A chave Organograma / Tabela — o mesmo desenho da chave Receitas / Despesas do painel. */
export function TeamMapViewToggle({
  value,
  onChange,
}: {
  value: TeamMapProps<TeamMapNodeShape>['view'];
  onChange: (view: TeamMapProps<TeamMapNodeShape>['view']) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Visão">
      {(['organograma', 'tabela'] as const).map((v) => (
        <Button
          key={v}
          variant={value === v ? 'secundario' : 'discreto'}
          size="sm"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
        >
          {v === 'organograma' ? 'Organograma' : 'Tabela'}
        </Button>
      ))}
    </div>
  );
}
